import {EventEmitter, Injectable} from '@angular/core';
import {UserSettingsService} from '../../user-settings.service';
import {isVoid} from '../../utils';

// Which of the grid's sections the user may hide. Portfolios / Positions / Hedge Totals are never
// on this list (owner ruling 2026-09-14: the portfolio section is constant).
export type ApgHidableSection = 'settings' | 'strategies' | 'pricebox' | 'zones-grid';

export const ApgHidableSections: ApgHidableSection[] = ['settings', 'strategies', 'pricebox', 'zones-grid'];

export type ApgSectionVisibility = Record<ApgHidableSection, boolean>;

export type ApgGridOrientation = 'left' | 'right' | undefined;

// One key per panel TYPE for the single APG (every open instance shares it), and one per SIDE for the
// Adjustment Comparison Grid — the same split the portfolios store uses (apg.* / cpg.*.left|right).
const StorageKey = 'apg.section-visibility';
const ComparisonStorageKeyLeft = 'cpg.section-visibility.left';
const ComparisonStorageKeyRight = 'cpg.section-visibility.right';

export function makeDefaultSectionVisibility(): ApgSectionVisibility {
    return {
        'settings': true,
        'strategies': true,
        'pricebox': true,
        'zones-grid': true
    };
}

// Root singleton on purpose: the change stream is how sibling grid instances (several APG panels,
// or the two comparison sides) learn about a save made in any one of them.
@Injectable({providedIn: 'root'})
export class ApgSectionVisibilityService {

    constructor(private readonly _userSettingsService: UserSettingsService) {
    }

    readonly changed$ = new EventEmitter<{ orientation: ApgGridOrientation, visibility: ApgSectionVisibility }>();

    getVisibility(orientation: ApgGridOrientation): ApgSectionVisibility {
        const key = this.makeStorageKey(orientation);
        const stored = this._userSettingsService.getValue<Partial<ApgSectionVisibility>>(key);
        const result = makeDefaultSectionVisibility();
        if (isVoid(stored)) {
            return result;
        }
        ApgHidableSections.forEach(section => {
            if (stored[section] === false) {
                result[section] = false;
            }
        });
        return result;
    }

    saveVisibility(orientation: ApgGridOrientation, visibility: ApgSectionVisibility): void {
        const key = this.makeStorageKey(orientation);
        const copy = {...visibility};
        this._userSettingsService.setValue(key, copy);
        this.changed$.emit({orientation, visibility: copy});
    }

    private makeStorageKey(orientation: ApgGridOrientation): string {
        if (orientation === 'left') {
            return ComparisonStorageKeyLeft;
        }
        if (orientation === 'right') {
            return ComparisonStorageKeyRight;
        }
        return StorageKey;
    }
}
